import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Download, CalendarDays, ChevronRight, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatIDR } from "@/lib/format";

// Tanggal kalender WIB (Asia/Jakarta, UTC+7) — selaras dengan backend
export const localISO = (d) => new Date(d.getTime() + 7 * 3600 * 1000).toISOString().slice(0, 10);

export const PERIOD_PRESETS = [
  { id: "today", label: "Hari Ini" },
  { id: "yesterday", label: "Kemarin" },
  { id: "7d", label: "7 Hari Terakhir" },
  { id: "30d", label: "30 Hari Terakhir" },
  { id: "month", label: "Bulan Ini" },
  { id: "all", label: "Semua" },
  { id: "custom", label: "Kustom" },
];

export function periodRange(id, customFrom, customTo) {
  const now = new Date();
  const today = localISO(now);
  if (id === "today") return { from: today, to: today };
  if (id === "yesterday") {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const s = localISO(y);
    return { from: s, to: s };
  }
  if (id === "7d") {
    const s = new Date(now);
    s.setDate(s.getDate() - 6);
    return { from: localISO(s), to: today };
  }
  if (id === "30d") {
    const s = new Date(now);
    s.setDate(s.getDate() - 29);
    return { from: localISO(s), to: today };
  }
  if (id === "month") return { from: today.slice(0, 8) + "01", to: today };
  if (id === "all") return { from: "2020-01-01", to: today };
  return { from: customFrom || today, to: customTo || today };
}

export function PeriodPicker({ preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, testid = "period" }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {PERIOD_PRESETS.map((p) => (
        <button
          key={p.id}
          data-testid={`${testid}-${p.id}`}
          onClick={() => setPreset(p.id)}
          className={`h-9 px-3.5 rounded-full text-xs font-medium transition-colors ${preset === p.id ? "bg-violet-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-violet-300"}`}
        >
          {p.label}
        </button>
      ))}
      {preset === "custom" && (
        <div className="flex items-center gap-1.5">
          <input data-testid={`${testid}-from`} type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-xs text-slate-700 bg-white" />
          <span className="text-slate-400 text-xs">s/d</span>
          <input data-testid={`${testid}-to`} type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-xs text-slate-700 bg-white" />
        </div>
      )}
    </div>
  );
}

export function DeltaBadge({ current, previous }) {
  if (!previous) return <span className="text-[11px] text-slate-400">vs periode sebelumnya —</span>;
  const pct = ((current - previous) / previous) * 100;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? "text-emerald-600" : "text-red-500"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(pct).toFixed(1).replace(".", ",")}%
      <span className="text-slate-400 font-normal ml-1">vs periode sebelumnya</span>
    </span>
  );
}

export function downloadCSV(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function DownloadButton({ onClick, testid = "download-data" }) {
  return (
    <button data-testid={testid} onClick={onClick} className="h-9 px-3.5 rounded-lg bg-white border border-slate-200 text-xs font-medium text-slate-600 hover:border-violet-300 flex items-center gap-1.5 transition-colors">
      <Download className="w-3.5 h-3.5" /> Download Data
    </button>
  );
}

// ---------------------------------------------------------------------------
// Filter periode gaya Shopee (dipakai Dashboard & Penjualan Produk)
// ---------------------------------------------------------------------------
export const dispDate = (iso) => iso.split("-").reverse().join("-");

export function initPicked() {
  const today = localISO(new Date());
  return { day: today, week: today, month: today.slice(0, 7), year: today.slice(0, 4), from: today, to: today };
}

export function shopeeRange(mode, p) {
  const today = localISO(new Date());
  const yest = localISO(new Date(Date.now() - 86400000));
  if (mode === "realtime") return { from: today, to: today, label: "Real-time", sub: dispDate(today) };
  if (mode === "yesterday") return { from: yest, to: yest, label: "Kemarin", sub: dispDate(yest) };
  if (mode === "7d") {
    const s = localISO(new Date(Date.now() - 6 * 86400000));
    return { from: s, to: today, label: "7 hari sebelumnya", sub: `${dispDate(s)} - ${dispDate(today)}` };
  }
  if (mode === "30d") {
    const s = localISO(new Date(Date.now() - 29 * 86400000));
    return { from: s, to: today, label: "30 hari sebelumnya", sub: `${dispDate(s)} - ${dispDate(today)}` };
  }
  if (mode === "all") return { from: "2020-01-01", to: today, label: "Semua", sub: `${dispDate("2020-01-01")} - ${dispDate(today)}` };
  if (mode === "day") return { from: p.day, to: p.day, label: "Per Hari", sub: dispDate(p.day) };
  if (mode === "custom") {
    const f = p.from <= p.to ? p.from : p.to;
    const t = p.from <= p.to ? p.to : p.from;
    return { from: f, to: t, label: "Rentang Tanggal", sub: `${dispDate(f)} - ${dispDate(t)}` };
  }
  if (mode === "week") {
    const d = new Date(p.week + "T00:00:00Z");
    const monday = new Date(d);
    monday.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    const f = monday.toISOString().slice(0, 10);
    const t = sunday.toISOString().slice(0, 10);
    return { from: f, to: t, label: "Per Minggu", sub: `${dispDate(f)} - ${dispDate(t)}` };
  }
  if (mode === "month") {
    const [y, m] = p.month.split("-").map(Number);
    const last = String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0");
    return { from: `${p.month}-01`, to: `${p.month}-${last}`, label: "Per Bulan", sub: `${dispDate(`${p.month}-01`)} - ${dispDate(`${p.month}-${last}`)}` };
  }
  return { from: `${p.year}-01-01`, to: `${p.year}-12-31`, label: "Berdasarkan Tahun", sub: p.year };
}

const SHOPEE_SIMPLE_OPTS = [
  { id: "realtime", label: "Real-time" },
  { id: "yesterday", label: "Kemarin" },
  { id: "7d", label: "7 hari sebelumnya" },
  { id: "30d", label: "30 hari sebelumnya" },
];
const SHOPEE_PICKED_OPTS = [
  { id: "day", label: "Per Hari" },
  { id: "week", label: "Per Minggu" },
  { id: "month", label: "Per Bulan" },
  { id: "year", label: "Berdasarkan Tahun" },
  { id: "custom", label: "Rentang Tanggal" },
];

export function ShopeePeriodFilter({ mode, setMode, picked, setPicked, loading = false, withAll = false, testid = "pos-period" }) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState(null);
  const range = shopeeRange(mode, picked);
  const yearNow = Number(localISO(new Date()).slice(0, 4));
  const years = Array.from({ length: 5 }, (_, i) => String(yearNow - i));
  const choose = (id) => { setMode(id); setPanel(null); setOpen(false); };
  const inputCls = "h-9 px-2.5 rounded-lg border border-slate-200 text-xs text-slate-700 bg-white focus:outline-none focus:border-blue-400";
  const simpleOpts = withAll ? [...SHOPEE_SIMPLE_OPTS, { id: "all", label: "Semua" }] : SHOPEE_SIMPLE_OPTS;

  return (
    <div className="relative">
      <button data-testid={`${testid}-btn`} onClick={() => setOpen((o) => !o)} className="h-9 pl-3 pr-2.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 flex items-center gap-2 hover:border-slate-300 transition-colors">
        <span className="font-medium">{range.label}</span>
        <span className="text-slate-500">{range.sub} (GMT+07)</span>
        {loading && <span className="text-red-500 font-medium" data-testid={`${testid}-loading`}>Memperbarui</span>}
        <CalendarDays className="w-4 h-4 text-slate-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div data-testid={`${testid}-panel`} className="absolute left-0 top-11 z-40 bg-white border border-slate-200 rounded-xl shadow-xl flex w-[480px] max-w-[90vw] overflow-hidden">
            <div className="w-52 flex-shrink-0 border-r border-slate-100 py-1.5">
              {simpleOpts.map((o) => (
                <button key={o.id} data-testid={`${testid}-opt-${o.id}`} onClick={() => choose(o.id)}
                  className={`w-full text-left px-4 py-2.5 text-xs hover:bg-slate-50 ${mode === o.id ? "text-[#EE4D2D] font-semibold" : "text-slate-700"}`}>
                  {o.label}
                </button>
              ))}
              <div className="my-1 border-t border-slate-100" />
              {SHOPEE_PICKED_OPTS.map((o) => (
                <button key={o.id} data-testid={`${testid}-opt-${o.id}`} onClick={() => setPanel(o.id)}
                  className={`w-full text-left px-4 py-2.5 text-xs flex items-center justify-between hover:bg-slate-50 ${mode === o.id ? "text-[#EE4D2D] font-semibold" : "text-slate-700"}`}>
                  {o.label} <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                </button>
              ))}
            </div>
            <div className="flex-1 p-4 min-h-[180px]">
              {!panel && <div className="text-xs text-slate-500 text-right pt-1">{range.sub}</div>}
              {panel === "day" && (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500">Pilih tanggal</div>
                  <input type="date" data-testid={`${testid}-date`} value={picked.day} onChange={(e) => setPicked({ ...picked, day: e.target.value })} className={inputCls} />
                </div>
              )}
              {panel === "week" && (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500">Pilih tanggal dalam minggu (Senin–Minggu)</div>
                  <input type="date" data-testid={`${testid}-week`} value={picked.week} onChange={(e) => setPicked({ ...picked, week: e.target.value })} className={inputCls} />
                </div>
              )}
              {panel === "month" && (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500">Pilih bulan</div>
                  <input type="month" data-testid={`${testid}-month`} value={picked.month} onChange={(e) => setPicked({ ...picked, month: e.target.value })} className={inputCls} />
                </div>
              )}
              {panel === "year" && (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500">Pilih tahun</div>
                  <select data-testid={`${testid}-year`} value={picked.year} onChange={(e) => setPicked({ ...picked, year: e.target.value })} className={inputCls}>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              )}
              {panel === "custom" && (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500">Pilih rentang tanggal</div>
                  <div className="flex items-center gap-2">
                    <input type="date" data-testid={`${testid}-from`} value={picked.from} onChange={(e) => setPicked({ ...picked, from: e.target.value })} className={inputCls} />
                    <span className="text-xs text-slate-400">s/d</span>
                    <input type="date" data-testid={`${testid}-to`} value={picked.to} onChange={(e) => setPicked({ ...picked, to: e.target.value })} className={inputCls} />
                  </div>
                </div>
              )}
              {panel && (
                <button data-testid={`${testid}-apply`} onClick={() => choose(panel)} className="mt-3 h-8 px-4 rounded-lg bg-[#EE4D2D] hover:bg-[#d63f20] text-white text-xs font-semibold transition-colors">Terapkan</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal mutasi kas bersama (terminal kasir & halaman laporan)
// ---------------------------------------------------------------------------
const MUT_META = {
  cash_in: { title: "Catat Uang Masuk", hint: "Contoh: tambahan modal kembalian, uang tambahan dari owner, pengembalian uang operasional.", cta: "Simpan Uang Masuk" },
  cash_out: { title: "Catat Pengeluaran", hint: "Contoh: pembelian perlengkapan toko, biaya parkir, kebutuhan mendadak.", cta: "Simpan Pengeluaran" },
  deposit: { title: "Setor Tunai", hint: "Setoran tunai ke owner di tengah shift (di luar proses tutup kasir).", cta: "Simpan Setoran" },
  refund_cash: { title: "Refund Tunai", hint: "Pengembalian uang tunai ke pelanggan dari cash drawer. Wajib memilih pesanan yang sudah di-void.", cta: "Simpan Refund" },
  adjustment: { title: "Transaksi Penyesuaian", hint: "Koreksi saldo dengan alasan. Membutuhkan persetujuan owner. Isi nominal negatif (mis. -50000) untuk mengurangi kas.", cta: "Ajukan Penyesuaian" },
};

export function MutModal({ kind, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [voided, setVoided] = useState([]);
  const [refTxn, setRefTxn] = useState("");
  const [refQ, setRefQ] = useState("");
  const meta = MUT_META[kind];
  const inp = "w-full h-11 px-3 rounded-lg border border-rose-200 text-xs text-slate-700 bg-white focus:outline-none focus:border-rose-400";

  useEffect(() => {
    if (kind !== "refund_cash") return;
    const t = setTimeout(() => {
      const params = { status: "voided" };
      if (refQ.trim()) params.q = refQ.trim();
      api.get("/pos/transactions", { params }).then((r) => setVoided(r.data.filter((x) => !x.refunded)));
    }, refQ.trim() ? 250 : 0);
    return () => clearTimeout(t);
  }, [kind, refQ]);

  const pickTxn = (id) => {
    setRefTxn(id);
    const t = voided.find((x) => x.id === id);
    if (t) setAmount(String(t.total));
  };

  const save = async () => {
    const amt = parseInt(amount || 0);
    if (!amt) { toast.error("Nominal wajib diisi"); return; }
    if (kind !== "adjustment" && amt < 0) { toast.error("Nominal harus positif"); return; }
    if (kind === "refund_cash" && !refTxn) { toast.error("Pilih pesanan yang sudah di-void"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/pos/cash/mutations", { type: kind, amount: amt, note, ref_txn_id: kind === "refund_cash" ? refTxn : null });
      toast.success(data.status === "pending" ? "Penyesuaian diajukan — menunggu persetujuan owner" : "Mutasi tercatat");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="pos-mut-modal">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display font-bold text-slate-900">{meta.title}</div>
          <button data-testid="pos-mut-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-[11px] text-slate-400 mb-4">{meta.hint}</div>
        {kind === "refund_cash" && (
          <>
            <label className="text-xs text-slate-500">Pesanan yang sudah di-void</label>
            <input data-testid="pos-mut-ref-search" value={refQ} onChange={(e) => setRefQ(e.target.value)} placeholder="Ketik no. pesanan / nama pelanggan..." className={`${inp} mb-2`} />
            <select data-testid="pos-mut-ref-txn" value={refTxn} onChange={(e) => pickTxn(e.target.value)} className={`${inp} mb-3`}>
              <option value="">— Pilih pesanan void —</option>
              {voided.map((t) => (
                <option key={t.id} value={t.id}>{t.code} • {t.customer_name || "Umum"} • {formatIDR(t.total)} • {(t.payment_method || "").toUpperCase()}</option>
              ))}
            </select>
            {voided.length === 0 && <div className="text-[11px] text-amber-600 bg-amber-50 rounded-lg px-2.5 py-1.5 mb-3">Tidak ada pesanan void yang belum di-refund. Void pesanan dulu dari menu Kas Drawer &gt; Void Transaksi.</div>}
          </>
        )}
        <label className="text-xs text-slate-500">Nominal (Rp)</label>
        <input data-testid="pos-mut-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className={`${inp} mb-3`} />
        <label className="text-xs text-slate-500">Keterangan</label>
        <input data-testid="pos-mut-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tulis keterangan..." className={`${inp} mb-4`} />
        <button data-testid="pos-mut-save" onClick={save} disabled={saving} className="w-full h-11 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
          {saving ? "Menyimpan..." : meta.cta}
        </button>
      </div>
    </div>
  );
}

